(function piCaseRecommendationsModule(globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (globalScope?.customElements && globalScope?.HTMLElement) {
    api.registerPiCaseRecommendations(globalScope);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createPiCaseRecommendations() {
  const MODEL_FILTER_PARAM = 'filter.p.m.custom.phone_model';
  const TEMPLATE_VIEW = 'pi-case-recommendations';
  const METAOBJECT_GID_PREFIX = 'gid://shopify/Metaobject/';
  const PRODUCT_GID_PREFIX = 'gid://shopify/Product/';
  const MAX_PAGES = 100;
  const responseCache = new Map();

  function normalizeMetaobjectId(value) {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    if (normalized.startsWith(METAOBJECT_GID_PREFIX)) return normalized;
    return `${METAOBJECT_GID_PREFIX}${normalized}`;
  }

  function isMetaobjectGid(value) {
    return /^gid:\/\/shopify\/Metaobject\/[^/]+$/.test(String(value || '').trim());
  }

  function isProductGid(value) {
    return /^gid:\/\/shopify\/Product\/[^/]+$/.test(String(value || '').trim());
  }

  function parseMetaobjectTokens(value) {
    return new Set(
      String(value || '')
        .split('||')
        .map(normalizeMetaobjectId)
        .filter(isMetaobjectGid)
    );
  }

  function compactModelText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s\-_\/]+/g, '');
  }

  function resolveReferencedModel(models, productTitle, productHandle) {
    const uniqueModels = [];
    const seenIds = new Set();
    for (const model of models || []) {
      const id = normalizeMetaobjectId(model?.id);
      const name = String(model?.name || '').trim();
      if (!isMetaobjectGid(id) || !name || seenIds.has(id)) continue;
      seenIds.add(id);
      uniqueModels.push({ ...model, id, name });
    }

    if (uniqueModels.length === 0) return { model: null, reason: 'missing-model' };
    if (uniqueModels.length === 1) return { model: uniqueModels[0], reason: 'ready' };

    const title = String(productTitle || '').trim().toLowerCase();
    const compactTitle = compactModelText(productTitle);
    const compactHandle = compactModelText(productHandle);
    const matches = uniqueModels
      .map((model) => {
        const name = model.name.toLowerCase();
        const compactName = compactModelText(model.name);
        const matchesProduct =
          title.includes(name) ||
          compactTitle.includes(compactName) ||
          compactHandle.includes(compactName);
        return { model, score: matchesProduct ? compactName.length : 0 };
      })
      .filter((match) => match.score > 0)
      .sort((left, right) => right.score - left.score);

    if (!matches.length || (matches[1] && matches[1].score === matches[0].score)) {
      return { model: null, reason: 'ambiguous-model' };
    }
    return { model: matches[0].model, reason: 'ready' };
  }

  function normalizeCollectionSourceUrl(sourceUrl, origin) {
    const rawSourceUrl = String(sourceUrl || '').trim();
    const baseOrigin =
      origin || (typeof location !== 'undefined' ? location.origin : '');
    if (!rawSourceUrl || !baseOrigin) {
      throw new Error('invalid-collection-url');
    }

    let normalizedSourceUrl = rawSourceUrl;
    if (rawSourceUrl.startsWith('shopify://collections/')) {
      const collectionHandle = rawSourceUrl
        .slice('shopify://collections/'.length)
        .split(/[/?#]/)[0];
      normalizedSourceUrl = `/collections/${collectionHandle}`;
    }

    const url = new URL(normalizedSourceUrl, baseOrigin);
    if (url.origin !== baseOrigin) {
      throw new Error('invalid-collection-origin');
    }
    if (!/\/collections\/[^/]+\/?$/.test(url.pathname)) {
      throw new Error('invalid-collection-url');
    }
    return url;
  }

  function buildRecommendationsUrl(sourceUrl, modelId, page = 1, origin) {
    const normalizedModelId = normalizeMetaobjectId(modelId);
    if (!isMetaobjectGid(normalizedModelId)) {
      throw new Error('invalid-model-gid');
    }

    const url = normalizeCollectionSourceUrl(sourceUrl, origin);

    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('view', TEMPLATE_VIEW);
    url.searchParams.set(MODEL_FILTER_PARAM, normalizedModelId);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    return url;
  }

  function createSelectionState() {
    return {
      seenProductIds: new Set(),
      seenDesignIds: new Set(),
    };
  }

  function selectRecommendationCards(cards, options = {}) {
    const modelId = normalizeMetaobjectId(options.modelId);
    const currentProductId = String(options.currentProductId || '').trim();
    const currentDesignId = normalizeMetaobjectId(options.currentDesignId);
    const limit = Math.max(1, Number.parseInt(options.limit, 10) || 10);
    const state = options.state || createSelectionState();
    const accepted = [];
    const rejected = {
      unavailable: 0,
      missingProduct: 0,
      currentProduct: 0,
      modelMismatch: 0,
      missingDesign: 0,
      hiddenDesign: 0,
      currentDesign: 0,
      duplicateProduct: 0,
      duplicateDesign: 0,
    };

    for (const card of cards || []) {
      if (accepted.length >= limit) break;

      const productId = String(card?.dataset?.productId || '').trim();
      const designId = normalizeMetaobjectId(card?.dataset?.designId);
      const modelIds = parseMetaobjectTokens(card?.dataset?.modelIds);

      if (card?.dataset?.productAvailable !== 'true') {
        rejected.unavailable += 1;
      } else if (!isProductGid(productId)) {
        rejected.missingProduct += 1;
      } else if (productId === currentProductId) {
        rejected.currentProduct += 1;
      } else if (!modelIds.has(modelId)) {
        rejected.modelMismatch += 1;
      } else if (!isMetaobjectGid(designId)) {
        rejected.missingDesign += 1;
      } else if (card?.dataset?.designHidden === 'true') {
        rejected.hiddenDesign += 1;
      } else if (currentDesignId && designId === currentDesignId) {
        rejected.currentDesign += 1;
      } else if (state.seenProductIds.has(productId)) {
        rejected.duplicateProduct += 1;
      } else if (state.seenDesignIds.has(designId)) {
        rejected.duplicateDesign += 1;
      } else {
        state.seenProductIds.add(productId);
        state.seenDesignIds.add(designId);
        accepted.push(card);
      }
    }

    return { accepted, rejected, state };
  }

  async function collectRecommendationPages(options = {}) {
    const limit = Math.max(1, Number.parseInt(options.limit, 10) || 10);
    const maxPages = Math.max(1, Number.parseInt(options.maxPages, 10) || MAX_PAGES);
    const state = createSelectionState();
    const cards = [];
    const diagnostics = [];
    let page = 1;
    let pages = 1;
    let modelFilterActive = true;

    if (typeof options.fetchPage !== 'function') {
      throw new TypeError('fetchPage is required');
    }

    do {
      const result = await options.fetchPage(page);
      pages = Math.min(Math.max(Number.parseInt(result?.pages, 10) || 1, 1), maxPages);
      const pageFilterActive = result?.modelFilterActive !== false;
      modelFilterActive = modelFilterActive && pageFilterActive;
      const selection = selectRecommendationCards(result?.cards, {
        modelId: options.modelId,
        currentProductId: options.currentProductId,
        currentDesignId: options.currentDesignId,
        limit: limit - cards.length,
        state,
      });
      diagnostics.push({
        page,
        url: String(result?.url || ''),
        modelFilterActive: pageFilterActive,
        collectionHandle: String(result?.collectionHandle || ''),
        productsCount: Number.parseInt(result?.productsCount, 10) || 0,
        rejected: selection.rejected,
      });
      cards.push(...selection.accepted);
      page += 1;
    } while (cards.length < limit && page <= pages);

    return { cards, diagnostics, pages, modelFilterActive };
  }

  async function requestPage(windowObject, url, signal) {
    const cacheKey = url.toString();
    if (responseCache.has(cacheKey)) {
      return responseCache.get(cacheKey);
    }

    const response = await windowObject.fetch(cacheKey, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) {
      const error = new Error(`request-failed-${response.status}`);
      error.code = 'request-failed';
      throw error;
    }

    const html = await response.text();
    responseCache.set(cacheKey, html);
    return html;
  }

  function isModelFilterActive(dataset) {
    return dataset?.modelFilterActive === 'true';
  }

  function parseResponsePage(windowObject, html) {
    const parsed = new windowObject.DOMParser().parseFromString(html, 'text/html');
    const response = parsed.querySelector('[data-pi-case-recommendations-response]');
    if (!response) {
      const error = new Error('response-root-missing');
      error.code = 'invalid-response';
      throw error;
    }
    return {
      cards: Array.from(response.querySelectorAll('[data-pi-love-card]')),
      page: Number.parseInt(response.dataset.page, 10) || 1,
      pages: Number.parseInt(response.dataset.pages, 10) || 1,
      modelFilterActive: isModelFilterActive(response.dataset),
      collectionHandle: String(response.dataset.collectionHandle || ''),
      productsCount: Number.parseInt(response.dataset.productsCount, 10) || 0,
    };
  }

  function summarizeDiagnostics(diagnostics) {
    const rejected = {};
    for (const diagnostic of diagnostics || []) {
      for (const [reason, count] of Object.entries(diagnostic?.rejected || {})) {
        rejected[reason] = (rejected[reason] || 0) + count;
      }
    }
    return rejected;
  }

  function registerPiCaseRecommendations(windowObject) {
    if (windowObject.customElements.get('pi-case-recommendations')) return;

    class PiCaseRecommendations extends windowObject.HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new windowObject.AbortController();
        this.generation = (this.generation || 0) + 1;
        this.section = this.closest('[data-pi-love-section]');

        if (!this.hasValidContract()) {
          this.hide(this.section?.dataset.piLoveServerReason || 'invalid-contract');
          return;
        }

        if (!('IntersectionObserver' in windowObject)) {
          this.load();
          return;
        }

        this.observer?.disconnect();
        this.observer = new windowObject.IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer?.disconnect();
            this.load();
          },
          { rootMargin: '300px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.controller?.abort();
        this.controller = null;
        this.generation = (this.generation || 0) + 1;
      }

      hasValidContract() {
        try {
          buildRecommendationsUrl(
            this.dataset.sourceUrl,
            this.dataset.modelId,
            1,
            windowObject.location.origin
          );
          return isProductGid(this.dataset.currentProductId);
        } catch (error) {
          return false;
        }
      }

      async load() {
        const generation = this.generation;
        const signal = this.controller.signal;
        const limit = Math.min(
          Math.max(Number.parseInt(this.dataset.limit, 10) || 10, 1),
          10
        );

        this.section?.removeAttribute('hidden');
        this.dataset.state = 'loading';
        this.setAttribute('aria-busy', 'true');

        try {
          const result = await collectRecommendationPages({
            modelId: this.dataset.modelId,
            currentProductId: this.dataset.currentProductId,
            currentDesignId: this.dataset.currentDesignId,
            limit,
            maxPages: MAX_PAGES,
            fetchPage: async (page) => {
              const url = buildRecommendationsUrl(
                this.dataset.sourceUrl,
                this.dataset.modelId,
                page,
                windowObject.location.origin
              );
              const html = await requestPage(windowObject, url, signal);
              return {
                ...parseResponsePage(windowObject, html),
                url: url.toString(),
              };
            },
          });
          if (generation !== this.generation) return;
          const diagnosticSummary = {
            modelId: this.dataset.modelId,
            modelName: this.section?.dataset.piLoveModelName || '',
            sourceUrl: this.dataset.sourceUrl,
            modelFilterActive: result.modelFilterActive,
            pagesScanned: result.diagnostics.length,
            collectionHandle: result.diagnostics[0]?.collectionHandle || '',
            endpointProductsCount: result.diagnostics[0]?.productsCount || 0,
            requestUrls: result.diagnostics.map((diagnostic) => diagnostic.url),
            rejected: summarizeDiagnostics(result.diagnostics),
          };
          this.dataset.modelFilterActive = String(result.modelFilterActive);

          if (!result.modelFilterActive) {
            console.warn(
              '[PI case recommendations] Storefront filter inactive; using exact client-side GID validation.',
              diagnosticSummary
            );
          }
          if (result.cards.length === 0) {
            this.hide('no-valid-cards', {
              ...diagnosticSummary,
              pagesAvailable: result.pages,
            });
            return;
          }

          const grid = windowObject.document.createElement('div');
          grid.className = 'pi-love__grid';
          grid.replaceChildren(...result.cards.map((card) => card.cloneNode(true)));
          this.replaceChildren(grid);
          this.dataset.state = 'ready';
          this.removeAttribute('aria-busy');
          this.section?.removeAttribute('hidden');
          this.section?.removeAttribute('data-pi-love-runtime-reason');
          console.info('[PI case recommendations] ready', {
            ...diagnosticSummary,
            rendered: result.cards.length,
          });
        } catch (error) {
          if (error?.name === 'AbortError' || generation !== this.generation) return;
          this.hide(error?.code || error?.message || 'request-error', error);
        }
      }

      hide(reason, detail) {
        this.replaceChildren();
        this.dataset.state = 'hidden';
        this.dataset.hiddenReason = reason;
        this.removeAttribute('aria-busy');
        this.section?.setAttribute('hidden', '');
        if (this.section) {
          this.section.dataset.piLoveRuntimeReason = reason;
        }

        const log = reason === 'request-error' || reason === 'request-failed'
          ? console.error
          : console.warn;
        log.call(console, `[PI case recommendations] hidden: ${reason}`, detail || '');
      }
    }

    windowObject.customElements.define('pi-case-recommendations', PiCaseRecommendations);
  }

  return {
    MODEL_FILTER_PARAM,
    TEMPLATE_VIEW,
    normalizeMetaobjectId,
    isMetaobjectGid,
    isProductGid,
    parseMetaobjectTokens,
    resolveReferencedModel,
    normalizeCollectionSourceUrl,
    buildRecommendationsUrl,
    createSelectionState,
    selectRecommendationCards,
    collectRecommendationPages,
    isModelFilterActive,
    summarizeDiagnostics,
    registerPiCaseRecommendations,
  };
});
